import React from 'react';
import { T, sx } from '../lib/tokens';
import { Wordmark } from '../components/ui/Wordmark';
import { useIsMobile } from '../lib/useIsMobile';

export function Terms() {
  const m = useIsMobile();
  return (
    <LegalShell title="Terms of service" subtitle="Last updated 18 April 2026" m={m}>
      <h2 style={h2}>Acceptance</h2>
      <p style={p}>
        By signing up or using receiptrec, you agree to these Terms and to our{' '}
        <a href="#/privacy" style={a}>Privacy Policy</a>. If you're using receiptrec on behalf
        of an organisation, you're confirming you have the authority to bind it.
      </p>

      <h2 style={h2}>What the service does</h2>
      <p style={p}>
        receiptrec ingests receipts from email, uploads and photos, uses AI to extract the
        important fields, and posts a matching Spend Money transaction to your connected Xero
        organisation.
      </p>

      <h2 style={h2}>Your Xero account</h2>
      <p style={p}>
        You stay responsible for the accuracy of what lands in your books. Review transactions
        regularly. We'll do our best, but we're not liable for accounting errors that flow from
        wrong receipts, wrong account mappings, or wrong GST treatments.
      </p>

      <h2 style={h2}>Subscriptions and billing</h2>
      <p style={p}>
        Paid plans are monthly recurring, billed in advance through Stripe. If you go over your
        plan's included receipts, the overage is billed at the end of the cycle. You can cancel
        any time from Settings - the service continues until the end of the period you've
        already paid for.
      </p>

      <h2 style={h2}>Refunds</h2>
      <p style={p}>
        If you buy a plan and decide it's not for you, email us within 14 days of first
        purchase and we'll issue a pro-rata refund. After 14 days we don't offer refunds,
        except where we've materially failed to deliver the service.
      </p>

      <h2 style={h2}>Free tier</h2>
      <p style={p}>
        The free tier includes 20 lifetime receipts. It's best-effort availability with no
        SLA. Good for trying us out, not for running your business on.
      </p>

      <h2 style={h2}>Acceptable use</h2>
      <p style={p}>
        Don't use receiptrec to send spam, process illegal content, reverse engineer the
        service, or access other users' accounts. Breach of this section is grounds for
        immediate suspension.
      </p>

      <h2 style={h2}>Fair use on Pro</h2>
      <p style={p}>
        The Pro plan carries a fair-use cap of 5,000 receipts per month. If you need more,
        we'll reach out and put together a custom plan - we're not going to cut you off
        without a conversation.
      </p>

      <h2 style={h2}>Liability cap</h2>
      <p style={p}>
        Our total liability to you for any claim arising out of the service is limited to the
        fees you paid us in the 12 months before the claim. We're not liable for indirect,
        consequential or loss-of-profits damages.
      </p>

      <h2 style={h2}>Termination</h2>
      <p style={p}>
        We may suspend or terminate your account for material breach of these Terms. You may
        cancel any time. On termination, we delete your data within 30 days, as described in
        the Privacy Policy.
      </p>

      <h2 style={h2}>Governing law</h2>
      <p style={p}>
        These Terms are governed by the laws of Victoria, Australia. Disputes fall under the
        exclusive jurisdiction of the Victorian courts.
      </p>

      <h2 style={h2}>Contact</h2>
      <p style={p}>
        Questions about the Terms? Email <a style={a} href="mailto:hello@receiptrec.com">hello@receiptrec.com</a>.
      </p>
    </LegalShell>
  );
}

const p: React.CSSProperties = { fontSize: 16, lineHeight: 1.6, color: T.graphite, margin: '0 0 18px' };
const h2: React.CSSProperties = { ...sx.serif, fontSize: 26, letterSpacing: '-0.015em', fontWeight: 400, margin: '36px 0 14px', color: T.graphite };
const a: React.CSSProperties = { color: T.sageDarker, textDecoration: 'underline' };

function LegalShell({ title, subtitle, m, children }: { title: string; subtitle?: string; m: boolean; children: React.ReactNode }) {
  return (
    <div style={{ background: T.paper, color: T.graphite, minHeight: '100vh' }}>
      <header style={{ position: 'sticky', top: 0, zIndex: 10, background: T.navy, borderBottom: `1px solid ${T.navyLine}` }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: m ? '14px 20px' : '18px 40px' }}>
          <a href="#/" style={{ textDecoration: 'none' }}><Wordmark size={m ? 20 : 24} /></a>
          <a href="#/" style={{ fontSize: 14, color: T.creamMuted, textDecoration: 'none' }}>Back to home</a>
        </div>
      </header>
      <main style={{ maxWidth: 720, margin: '0 auto', padding: m ? '32px 20px' : '60px 40px' }}>
        <h1 style={{ ...sx.serif, fontSize: m ? 32 : 48, lineHeight: 1.05, letterSpacing: '-0.03em', fontWeight: 400, margin: '0 0 10px' }}>{title}</h1>
        {subtitle && <div style={{ fontSize: 13, color: T.graphiteDim, marginBottom: 28 }}>{subtitle}</div>}
        {children}
      </main>
    </div>
  );
}
